import { CONNECTORS_WAGMI } from '@dapp/multi-lib/ui/wallet';
import { useToast } from '@dapp/multi-lib/ui/toast';
import { defaultNetwork, NetworkChainIds } from '@dapp/multi-lib/network';
import { providers } from 'ethers';
import React, { useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  setSelectedNetwork,
  switchChain,
} from 'store/features/chain/chainSlice';
import { updateSelectedWallet } from 'store/features/wallet/walletSlice';
import { useAppDispatch } from 'store/hooks';
import { APY_STORAGE_KEYS } from 'utils/ethereum';
import {
  useConnection,
  useConnect,
  useDisconnect,
  useWalletClient,
} from 'wagmi';

export type UserContext = {
  chainId: number;
  network: number;
  library: providers.JsonRpcProvider | providers.JsonRpcSigner;
  disconnect: () => Promise<void>;
  connect: (newConnector: CONNECTORS_WAGMI) => Promise<void>;
  account: `0x${string}`;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  connector: any;
};

const Context = React.createContext({});

const Provider = ({ children }: { children: React.ReactNode }) => {
  const dispatch = useAppDispatch();
  const toastV2 = useToast();
  const { t } = useTranslation('common');

  const { chain } = useConnection();
  const { address: account, connector } = useConnection();
  const { connect: wagmiConnect, connectors } = useConnect();
  const { disconnect: wagmiDisconnect } = useDisconnect();

  const chainId = chain?.id ?? defaultNetwork.id;
  const { data: walletClient } = useWalletClient({ chainId: chainId });

  // fix for migrating from web3 to wagmi
  const library = useMemo(() => {
    if (typeof window === 'undefined') {
      return;
    }

    return walletClient && walletClient.account && walletClient.account?.address
      ? walletClientToSigner(walletClient)?.provider
      : new providers.JsonRpcProvider(defaultNetwork.rpcUrls.default.http[0]);
  }, [walletClient]);

  // Connect using wagmi
  const connect = async (newConnector: CONNECTORS_WAGMI) => {
    try {
      const useThisConnector = connectors.find(
        (c) => c.id.toLowerCase() === newConnector
      );
      wagmiConnect({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        connector: useThisConnector as any,
      });
    } catch (error) {
      console.log('### error', error);
      dispatch(updateSelectedWallet({ wallet: undefined }));
      toastV2({
        title: t('ERROR_MESSAGES.ERROR'),
        description: t('ERROR_MESSAGES.WALLET_LOAD'),
        type: 'error',
      });
    }
  };

  const disconnect = async () => {
    wagmiDisconnect();
    dispatch(setSelectedNetwork(defaultNetwork.id));
    dispatch(updateSelectedWallet({ wallet: undefined }));
  };

  useEffect(() => {
    if ((chainId && account) || !chainId) {
      const chainIdWithType = chainId as NetworkChainIds;
      dispatch(switchChain(chainIdWithType));
      Object.keys(localStorage)
        .filter((k) =>
          Object.values(APY_STORAGE_KEYS).some((v) => k.includes(v))
        )
        .forEach((v) => {
          localStorage.removeItem(v);
        });
    }
  }, [chainId, library, account]);

  return (
    <Context.Provider
      value={{
        library: library,
        account: account,
        connect,
        disconnect,
        chainId: chainId,
        network: chainId ?? defaultNetwork.id,
        connector,
      }}
    >
      {children}
    </Context.Provider>
  );
};

// eslint-disable-next-line import/no-default-export
export default Context;
export const UserProvider = Provider;

/**
 * @deprecated use wagmi.useAccount
 */
export const useUser = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const context = useContext<any>(Context);
  if (context === null) throw 'No user context';
  return context;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function walletClientToSigner(walletClient: any, chainId?: any) {
  const { account, chain, transport } = walletClient;
  const network = {
    chainId: chain.id,
    name: chain.name,
    ensAddress: chain.contracts?.ensRegistry?.address,
  };
  const provider = new providers.Web3Provider(transport, network);
  const signer = provider.getSigner(account?.address || '');
  return signer;
}
